import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { SermonContentStyle } from "../../style/SermonContentStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faEye } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../util/hook";
import { ColumnData } from "../../contexts/ColumnContext";

interface ColumnViewProps {
  column: ColumnData;
}

const extractGoogleDriveFileId = (value?: string) => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const filePathMatch = trimmed.match(/\/file\/d\/([^/]+)/);
  if (filePathMatch?.[1]) return filePathMatch[1];

  try {
    const parsed = new URL(trimmed);
    return parsed.searchParams.get("id");
  } catch {
    return null;
  }
};

const normalizeImageUrl = (value?: string) => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  const driveFileId = extractGoogleDriveFileId(trimmed);
  if (driveFileId) {
    return `https://drive.google.com/thumbnail?id=${driveFileId}&sz=w1000`;
  }

  return trimmed;
};

const ColumnView = ({ column }: ColumnViewProps) => {
  const { isLoggedIn } = useAuth();
  const imageUrl = normalizeImageUrl(column.imageUrl);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <>
      <ColumnHeader>
        <ColumnTitle>{column.title}</ColumnTitle>
        <ColumnMeta>
          <MetaItem>
            <FontAwesomeIcon icon={faCalendarDays} />
            {column.date}
          </MetaItem>
          {isLoggedIn && (
            <MetaItem>
              <FontAwesomeIcon icon={faEye} />
              조회수 {(column as any).view_count || 0}
            </MetaItem>
          )}
        </ColumnMeta>
      </ColumnHeader>

      <ContentSection>
        {imageUrl && !imageFailed && (
          <HeroImageFrame>
            {!imageLoaded && <HeroImageSkeleton aria-hidden="true" />}
            <HeroImage
              src={imageUrl}
              alt={column.title}
              loading="lazy"
              $visible={imageLoaded}
              onLoad={() => {
                setImageLoaded(true);
              }}
              onError={(event) => {
                setImageFailed(true);
                event.currentTarget.style.display = "none";
              }}
            />
          </HeroImageFrame>
        )}
        <ContentBody
          dangerouslySetInnerHTML={{ __html: column.content || "" }}
        />
      </ContentSection>
    </>
  );
};

const ColumnHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #eee;
`;

const ColumnTitle = styled.h1`
  font-size: 32px;
  color: #004094;
  margin-bottom: 20px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 26px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const ColumnMeta = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;

  svg {
    color: #004094;
  }
`;

const ContentSection = styled.div`
  margin-bottom: 40px;
`;

const HeroImageFrame = styled.div`
  width: min(1000px, calc(100% + 160px));
  min-height: 360px;
  margin: 0 auto 32px;
  transform: translateX(-80px);
  border-radius: 10px;
  overflow: hidden;
  background: #f4f4f4;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    width: min(920px, calc(100% + 120px));
    transform: translateX(-60px);
  }

  @media (max-width: 768px) {
    width: 100%;
    min-height: 240px;
    margin-bottom: 24px;
    transform: none;
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const HeroImageSkeleton = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, #f3f3f3 0%, #e9e9e9 100%);

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.55) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${shimmer} 1.6s ease-in-out infinite;
  }
`;

const HeroImage = styled.img<{ $visible: boolean }>`
  display: block;
  width: 100%;
  height: auto;
  max-height: 560px;
  object-fit: contain;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    max-height: 360px;
    margin-bottom: 24px;
    border-radius: 10px;
  }
`;

const ContentBody = styled.div`
  ${SermonContentStyle}
`;

export default ColumnView;
